import { defaultLanguage, supportedLanguages, type SupportedLanguage } from './index'
import { contentRoutes } from './content-routes'

export const localizedRoutes = {
  home: { it: '', en: '' },
  about: { it: '#chi-siamo', en: '#chi-siamo' },
  solutions: { it: 'soluzioni-per-il-laboratorio', en: 'laboratory-solutions' },
  news: { it: 'news', en: 'news' },
  contacts: { it: 'contatti', en: 'contacts' },
  careers: { it: 'lavora-con-noi', en: 'careers' },
  certifications: { it: 'certificazioni', en: 'certifications' },
  privacy: { it: 'cookie-privacy', en: 'cookie-privacy' },
  cookies: { it: 'informativa-cookie', en: 'cookie-policy' },
  reserved: { it: 'area-riservata', en: 'reserved-area' },
} as const
export type RouteKey = keyof typeof localizedRoutes

export function languageFromPath(path: string): SupportedLanguage {
  return supportedLanguages.find((language) => language === path.split('/')[1]) ?? defaultLanguage
}
export function getLocalizedPath(key: RouteKey, lang: SupportedLanguage): string {
  const path = localizedRoutes[key][lang]
  return `/${lang}${path && !path.startsWith('#') ? '/' : ''}${path}`
}
export function findRouteKeyFromPath(path: string, lang: SupportedLanguage): RouteKey | null {
  return (Object.keys(localizedRoutes) as RouteKey[]).find((key) => localizedRoutes[key][lang] === path) ?? null
}
/** Accept a route key, stable content ID, localized URL, or external URL. */
export function resolveLocalizedPath(to: string, lang: SupportedLanguage): string {
  if (/^(https?:|mailto:|tel:)/.test(to)) return to
  if (to in localizedRoutes) return getLocalizedPath(to as RouteKey, lang)
  const record = contentRoutes.find((item) => item.id === to)
  if (record) return record[lang]
  if (supportedLanguages.some((locale) => to === `/${locale}` || to.startsWith(`/${locale}/`) || to.startsWith(`/${locale}#`))) return to
  if (to.startsWith('#') || to.startsWith('?')) return to
  return `/${lang}/${to.replace(/^\//, '')}`
}
/** Preserve identity, query and anchor, including records with a source translation gap. */
export function equivalentLocation(location: { pathname: string; search: string; hash: string }, target: SupportedLanguage): string {
  const source = languageFromPath(location.pathname)
  const pathname = location.pathname.replace(/\/$/, '')
  const query = new URLSearchParams(location.search)
  const record = contentRoutes.find((item) => item.type !== 'news' && item[source] === pathname)
  let nextPath = record?.[target]
  const rest = pathname.split('/').slice(2).join('/')
  if (!nextPath) {
    const key = findRouteKeyFromPath(rest, source)
    if (key) nextPath = getLocalizedPath(key, target)
    else if (rest === localizedRoutes.reserved[source] || rest.startsWith(`${localizedRoutes.reserved[source]}/`)) {
      nextPath = getLocalizedPath('reserved', target) + rest.slice(localizedRoutes.reserved[source].length)
    } else nextPath = `/${target}${rest ? `/${rest}` : ''}`
  }
  const slug = query.get('article')
  if (slug && rest === localizedRoutes.news[source]) {
    const article = contentRoutes.find((item) => item.type === 'news' && new URL(item[source], 'https://local.invalid').searchParams.get('article') === slug)
    if (article) query.set('article', new URL(article[target], 'https://local.invalid').searchParams.get('article')!)
  }
  return nextPath + (query.size ? `?${query}` : '') + location.hash
}
